import React, { useCallback, useEffect } from 'react';
import { useRouter } from 'expo-router';
import { StyleSheet, View } from 'react-native';

import { theme, useNavbarContainerHeight } from '@sg/garnish';

import { MenuTopDownCategories } from '@order/components-menu';
import { useTelemetry } from '@order/Telemetry';

import { useIsLoggedIn } from '../../../../context/AuthMachine';
import { type useMenuContent } from '../../hooks';
import { type MenuContentCategoryWithSlug } from '../../MenuContent.types';
import { useTrackAssetsLoad } from '../MenuContentTopDownModal/hooks';

// ─────────────────────────────────────────────────────────────────────────────

export const MenuContentTopDownView = (props: Props) => {
  const { categories, categoriesBadges, onCategoryPress, recentOrdersTargetId, withNavbarSpacing } =
    props;

  const router = useRouter();
  const isLoggedIn = useIsLoggedIn();
  const { trackEvent } = useTelemetry();
  const { trackAssetLoad } = useTrackAssetsLoad({ categories });
  const { paddingBottomStyle: navbarPaddingBottomStyle } = useNavbarContainerHeight();

  // ─── Derived ───────────────────────────────────────────────────────────────

  const categoriesContentContainerStyles = [
    styles.categoriesContentContainer,
    withNavbarSpacing ? navbarPaddingBottomStyle : undefined,
  ];

  // ─── Handlers ──────────────────────────────────────────────────────────────

  const handleCategoryPress = useCallback(
    (categorySlug: string) => {
      onCategoryPress(categorySlug);

      const targetCategoryIndex = categories.findIndex(
        (category) => category.slug === categorySlug,
      );
      const targetCategory = categories[targetCategoryIndex];

      if (!targetCategory) return;

      trackEvent({
        name: 'menu.top-down-categories.category-press',
        payload: {
          categoryName: targetCategory.name,
          categoryIndex: targetCategoryIndex,
        },
      });
    },
    [categories, onCategoryPress, trackEvent],
  );

  const handleRecentOrdersPress = useCallback(() => {
    trackEvent({ name: 'menu.top-down-categories.navigate-to-reorder-button-press' });

    if (recentOrdersTargetId) {
      onCategoryPress(recentOrdersTargetId);

      return;
    }

    router.navigate('/reorder');
  }, [onCategoryPress, recentOrdersTargetId, router, trackEvent]);

  // ─── Effects ───────────────────────────────────────────────────────────────

  useEffect(() => {
    trackEvent({ name: 'menu.top-down-categories.view' });
  }, [trackEvent]);

  // ───────────────────────────────────────────────────────────────────────────

  return (
    <View testID="menu-content.top-down-categories-view" style={styles.container}>
      <MenuTopDownCategories.Container
        style={styles.categoriesContainer}
        contentContainerStyle={categoriesContentContainerStyles}
      >
        {isLoggedIn ? (
          <MenuTopDownCategories.RecentOrdersButton
            width="100%"
            rightIcon="IconArrowRight"
            iconSize={24}
            style={styles.recentOrdersButton}
            onPress={handleRecentOrdersPress}
          />
        ) : null}

        {categories.map((category) => {
          const maybeCategoryImageSrc = category.products[0]?.asset?.url;
          const maybeCategoryBadge = categoriesBadges[category.id];

          return (
            <MenuTopDownCategories.Item.Pressable
              key={category.id}
              categoryId={category.slug}
              onPress={handleCategoryPress}
            >
              {maybeCategoryImageSrc ? (
                <MenuTopDownCategories.Item.Image
                  categoryId={category.id}
                  source={maybeCategoryImageSrc}
                  alt={category.name}
                  isCustomCategory={category.isCustom}
                  onLoad={trackAssetLoad}
                />
              ) : null}

              <MenuTopDownCategories.Item.DetailsContainer>
                <MenuTopDownCategories.Item.Title badge={maybeCategoryBadge}>
                  {category.name}
                </MenuTopDownCategories.Item.Title>

                {category.description ? (
                  <MenuTopDownCategories.Item.Description>
                    {category.description}
                  </MenuTopDownCategories.Item.Description>
                ) : null}
              </MenuTopDownCategories.Item.DetailsContainer>
            </MenuTopDownCategories.Item.Pressable>
          );
        })}
      </MenuTopDownCategories.Container>
    </View>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    ...StyleSheet.absoluteFill,
    backgroundColor: theme.colors.APP_BACKGROUND,
  },
  categoriesContainer: {
    flex: 1,
  },
  categoriesContentContainer: {
    paddingTop: theme.spacing['4'],
  },
  recentOrdersButton: {
    alignSelf: 'stretch',
    justifyContent: 'space-between',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type CategoriesBadges = ReturnType<typeof useMenuContent>['mappedContent']['categoriesBadges'];

type Props = {
  categories: MenuContentCategoryWithSlug[];
  categoriesBadges: CategoriesBadges;
  onCategoryPress: (categorySlug: string) => void;
  recentOrdersTargetId?: string;
  withNavbarSpacing: boolean;
};
